// SNIGJ - Main App Component

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { AppearanceProvider } from './context/AppearanceContext';
import AuthCallback from './components/AuthCallback';
import SecurityShield from './components/SecurityShield';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import CasesPage from './pages/CasesPage';
import CaseDetailPage from './pages/CaseDetailPage';
import PrisonersPage from './pages/PrisonersPage';
import PrisonerDetailPage from './pages/PrisonerDetailPage';
import PrisonerReleasesPage from './pages/PrisonerReleasesPage';
import DocumentsPage from './pages/DocumentsPage';
import PaymentsPage from './pages/PaymentsPage';
import BorderControlPage from './pages/BorderControlPage';
import ClemencyPage from './pages/ClemencyPage';
import AdminPage from './pages/AdminPage';
import AuditLogsPage from './pages/AuditLogsPage';
import SettingsPage from './pages/SettingsPage';
import CitizenPortalPage from './pages/CitizenPortalPage';
import SystemConfigPage from './pages/SystemConfigPage';
import AccountingPage from './pages/AccountingPage';
import TrialTrackingPage from './pages/TrialTrackingPage';
import DocumentTemplatesPage from './pages/DocumentTemplatesPage';
import CitizenSubmissionsPage from './pages/CitizenSubmissionsPage';
import AdminCurrencyPage from './pages/AdminCurrencyPage';
import AdminTranslationsPage from './pages/AdminTranslationsPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import JudicialMapPage from './pages/JudicialMapPage';
import DocumentSigningPage from './pages/DocumentSigningPage';
import LawyerDashboardPage from './pages/LawyerDashboardPage';
import BorderPolicePage from './pages/BorderPolicePage';
import StatisticsPage from './pages/StatisticsPage';
import AdminUserManagementPage from './pages/AdminUserManagementPage';
import CitizenMobileApp from './pages/CitizenMobileApp';
import RequisitionPage from './pages/RequisitionPage';
import AdminBrandingPage from './pages/AdminBrandingPage';
import HomePage from './pages/HomePage';
import CashRegisterPage from './pages/CashRegisterPage';
import AdminCashOverviewPage from './pages/AdminCashOverviewPage';
import TrialManagementPage from './pages/TrialManagementPage';
import AdminStructuresPage from './pages/AdminStructuresPage';
import AdminOffensesPage from './pages/AdminOffensesPage';
import AdminDataManagementPage from './pages/AdminDataManagementPage';
import ReportsPage from './pages/ReportsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import LegalDocumentsPage from './pages/LegalDocumentsPage';
import AdminWidgetsPage from './pages/AdminWidgetsPage';
import AdminSeoPage from './pages/AdminSeoPage';
import AdminSystemConfigPage from './pages/AdminSystemConfigPage';
import AdminSMTPConfigPage from './pages/AdminSMTPConfigPage';
import LegalAIAssistant from './components/assistant/LegalAIAssistant';
import LegalLibraryPage from './pages/LegalLibraryPage';
import MyProfilePage from './pages/MyProfilePage';
import PrisonsListPage from './pages/PrisonsListPage';
import PrisonEditPage from './pages/PrisonEditPage';
import SupervisionPage from './pages/SupervisionPage';
import AdminSecurityConfigPage from './pages/AdminSecurityConfigPage';
import AdminPaymentConfigPage from './pages/AdminPaymentConfigPage';
import AdminEmailHistoryPage from './pages/AdminEmailHistoryPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import PoliceWorkPage from './pages/PoliceWorkPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data was passed from AuthCallback, skip loading check
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    // Store the intended path for redirect after login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  // This must happen synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Protected Routes */}
      <Route path="/home" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/cash-register" element={
        <ProtectedRoute>
          <CashRegisterPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/cash-overview" element={
        <ProtectedRoute>
          <AdminCashOverviewPage />
        </ProtectedRoute>
      } />
      <Route path="/citizen-portal" element={
        <ProtectedRoute>
          <CitizenPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/cases" element={
        <ProtectedRoute>
          <CasesPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/new" element={
        <ProtectedRoute>
          <CaseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/:caseId" element={
        <ProtectedRoute>
          <CaseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/:caseId/edit" element={
        <ProtectedRoute>
          <CaseDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/prisoners" element={
        <ProtectedRoute>
          <PrisonersPage />
        </ProtectedRoute>
      } />
      <Route path="/prisoners/releases" element={
        <ProtectedRoute>
          <PrisonerReleasesPage />
        </ProtectedRoute>
      } />
      <Route path="/prisoners/new" element={
        <ProtectedRoute>
          <PrisonerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/prisoners/:prisonerId" element={
        <ProtectedRoute>
          <PrisonerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/prisoners/:prisonerId/edit" element={
        <ProtectedRoute>
          <PrisonerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/documents/upload" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute>
          <PaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/payments/success" element={
        <ProtectedRoute>
          <PaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/border-control" element={
        <ProtectedRoute>
          <BorderControlPage />
        </ProtectedRoute>
      } />
      <Route path="/clemency" element={
        <ProtectedRoute>
          <ClemencyPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminUserManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/config" element={
        <ProtectedRoute>
          <SystemConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/accounting" element={
        <ProtectedRoute>
          <AccountingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/document-templates" element={
        <ProtectedRoute>
          <DocumentTemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/cases/:caseId/trial" element={
        <ProtectedRoute>
          <TrialTrackingPage />
        </ProtectedRoute>
      } />
      <Route path="/audit-logs" element={
        <ProtectedRoute>
          <AuditLogsPage />
        </ProtectedRoute>
      } />
      <Route path="/citizen/submissions" element={
        <ProtectedRoute>
          <CitizenSubmissionsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/my-profile" element={
        <ProtectedRoute>
          <MyProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/currency" element={
        <ProtectedRoute>
          <AdminCurrencyPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/translations" element={
        <ProtectedRoute>
          <AdminTranslationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/branding" element={
        <ProtectedRoute>
          <AdminBrandingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/widgets" element={
        <ProtectedRoute>
          <AdminWidgetsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/seo" element={
        <ProtectedRoute>
          <AdminSeoPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/system-config" element={
        <ProtectedRoute>
          <AdminSystemConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/smtp-config" element={
        <ProtectedRoute>
          <AdminSMTPConfigPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/structures" element={
        <ProtectedRoute>
          <AdminStructuresPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/offenses" element={
        <ProtectedRoute>
          <AdminOffensesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/data-management" element={
        <ProtectedRoute>
          <AdminDataManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute>
          <PaymentCancelPage />
        </ProtectedRoute>
      } />
      <Route path="/map" element={
        <ProtectedRoute>
          <JudicialMapPage />
        </ProtectedRoute>
      } />
      <Route path="/documents/:documentId/sign" element={
        <ProtectedRoute>
          <DocumentSigningPage />
        </ProtectedRoute>
      } />
      <Route path="/lawyer" element={
        <ProtectedRoute>
          <LawyerDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/border-police" element={
        <ProtectedRoute>
          <BorderPolicePage />
        </ProtectedRoute>
      } />
      <Route path="/statistics" element={
        <ProtectedRoute>
          <StatisticsPage />
        </ProtectedRoute>
      } />
      <Route path="/requisitions" element={
        <ProtectedRoute>
          <RequisitionPage />
        </ProtectedRoute>
      } />
      <Route path="/trials" element={
        <ProtectedRoute>
          <TrialManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/complaints" element={
        <ProtectedRoute>
          <ComplaintsPage />
        </ProtectedRoute>
      } />
      <Route path="/legal-documents" element={
        <ProtectedRoute>
          <LegalDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/legal-library" element={
        <ProtectedRoute>
          <LegalLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/prisons" element={
        <ProtectedRoute>
          <PrisonsListPage />
        </ProtectedRoute>
      } />
      <Route path="/prisons/:prisonId/edit" element={
        <ProtectedRoute>
          <PrisonEditPage />
        </ProtectedRoute>
      } />

      <Route path="/supervision" element={
        <ProtectedRoute>
          <SupervisionPage />
        </ProtectedRoute>
      } />

      <Route path="/admin/security" element={
        <ProtectedRoute>
          <AdminSecurityConfigPage />
        </ProtectedRoute>
      } />

      <Route path="/admin/payment-config" element={
        <ProtectedRoute>
          <AdminPaymentConfigPage />
        </ProtectedRoute>
      } />

      <Route path="/admin/email-history" element={
        <ProtectedRoute>
          <AdminEmailHistoryPage />
        </ProtectedRoute>
      } />

      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/police-work" element={
        <ProtectedRoute>
          <PoliceWorkPage />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
};

// AI Assistant wrapper - only shows when user is logged in
const AIAssistantWrapper = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <LegalAIAssistant />;
};

function App() {
  // Check if we're in development mode - disable security shield for easier debugging
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <ThemeProvider>
      <AppearanceProvider>
        <AuthProvider>
          <CurrencyProvider>
            {isDevelopment ? (
              <BrowserRouter>
                <AppRouter />
                <AIAssistantWrapper />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            ) : (
              <SecurityShield>
                <BrowserRouter>
                  <AppRouter />
                  <AIAssistantWrapper />
                  <Toaster position="top-right" richColors />
                </BrowserRouter>
              </SecurityShield>
            )}
          </CurrencyProvider>
        </AuthProvider>
      </AppearanceProvider>
    </ThemeProvider>
  );
}

export default App;
